import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components/macro';
import { SwiperSlide } from 'swiper/react';
import { Swiper } from 'swiper/types';
import { WxText } from '../..';
import { HomeContent } from '../../../hooks/api/queries/useHome';
import { breakpoint } from '../../../styles/constants';
import { IMAGE_RATIOS } from '../../../styles/imageRatios';
import { appendCommitHash } from '../../../utils/appendHash';
import { Carousel } from '../../ui/carousel/Carousel';
import { CarouselControls } from '../../ui/carousel/CarouselControls';
import { WImage } from '../../ui/image/Image';

interface Props {
  item: HomeContent;
}

export const HomeHero = ({ item }: Props) => {
  const [swiper, setSwiper] = useState<Swiper>();

  const promos = item?.promos?.filter(
    (i) => i?.localizedImage?.path || i?.imageDefault?.path,
  );

  if ((promos?.length ?? 0) === 0) return null;

  return (
    <Wrapper>
      <Carousel
        setSwiper={setSwiper}
        spaceBetween={0}
        loop
        autoplay
        noSwiping={promos?.length === 1}
      >
        {promos
          ?.filter((a) => a)
          ?.sort((a, b) => {
            if (a.sorting === null) return 1;
            else if (b.sorting === null) return -1;
            else return (a?.sorting ?? 0) - (b?.sorting ?? 0);
          })
          ?.map((i, index) => (
            <SwiperSlide key={`home-hero-${index}`}>
              <HeroDiv>
                {i.destinationUrl || i.defaultDestinationUrl ? (
                  <Link to={appendCommitHash(i?.destinationUrl ?? '')}>
                    <WImage image={i.localizedImage ?? i.imageDefault} />
                  </Link>
                ) : (
                  <WImage image={i.localizedImage ?? i.imageDefault} />
                )}
                <TextDiv>
                  <WxText text={i.title} bold uppercase />
                </TextDiv>
              </HeroDiv>
            </SwiperSlide>
          ))}
      </Carousel>

      <ControlsDiv>
        {promos?.length && promos.length > 1 && (
          <CarouselControls swiper={swiper} length={0} black />
        )}
      </ControlsDiv>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: var(--viewportWidth);
  margin-inline: 50%;
  transform: translateX(-50%);
  margin-bottom: 3.75rem;

  > :first-child {
    width: 100%;
    > div {
      height: 100%;
    }
  }

  img {
    aspect-ratio: ${IMAGE_RATIOS.HOME_HERO_MOBILE};
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (min-width: ${breakpoint}) {
    img {
      aspect-ratio: ${IMAGE_RATIOS.HOME_HERO};
    }
  }
`;

const HeroDiv = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;

  > :first-child {
    flex: 1;
  }
`;

const TextDiv = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  position: relative;
  min-height: 70px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  top: -3px;

  margin: 0 auto;
  padding: 1.25rem 0.9375rem;

  p {
    margin-right: 80px;
  }

  @media (min-width: ${breakpoint}) {
    padding-inline: calc((var(--viewportWidth) - ${breakpoint}) / 2);
    p {
      margin-right: unset;
    }
  }
`;

const ControlsDiv = styled.div`
  position: absolute;
  bottom: 35px;
  transform: translateY(50%);
  right: 0.9375rem;
  z-index: 2;

  @media (min-width: ${breakpoint}) {
    right: calc((var(--viewportWidth) - ${breakpoint}) / 2);
  }
`;
